/**
 * Mobile Admin Entry — passcode-only standalone unlock for phones.
 *
 * Bookmark this URL: /m
 *   Enter the admin passcode → 30-day session created.
 *   You can then use /admin and /command-center freely on your phone
 *   without dealing with Google OAuth cookie quirks on Safari.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, KeyRound, ShieldCheck, ChevronLeft, Activity, Calendar, Wallet, Users } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MobileAdminUnlock() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  // If a cookie session already exists, skip the lock screen
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (r.ok && !cancelled) setUnlocked(true);
      } catch { /* not signed in */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    if ((code || '').length < 1) { toast.error('Enter the passcode'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/auth/passcode-unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ passcode: code.trim(), label: navigator.userAgent.slice(0, 80) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Invalid passcode');
      // Persist locally as a belt-and-braces fallback (Authorization header)
      localStorage.setItem('ace_admin_token', data.session_token);
      toast.success('Unlocked. 30-day session created.', { position: 'top-center' });
      setUnlocked(true);
    } catch (e) {
      toast.error(e.message);
      setCode('');
    }
    setBusy(false);
  };

  if (unlocked) return <UnlockedHub />;

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#0A0A0A] flex flex-col" data-testid="mobile-admin-unlock">
      <Toaster position="top-center" theme="light" />

      <div className="px-5 pt-8 pb-6 border-b border-[#E5E5E5] bg-white">
        <Link to="/" className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] text-[#666] hover:text-[#0A0A0A] font-bold mb-3">
          <ChevronLeft className="h-3 w-3 mr-1" /> Back
        </Link>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#E52B20] font-bold">ACE · Mobile Admin</p>
        <h1 className="text-3xl font-black tracking-tight mt-2">Quick Unlock</h1>
        <p className="text-xs text-[#666] mt-1">Skip Google sign-in. Passcode = 30 days on this phone.</p>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10">
        <KeyRound className="h-12 w-12 text-[#E5E5E5] mb-6" strokeWidth={1.5} />

        <label className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mb-2 self-start w-full max-w-sm">Admin Passcode</label>
        <input
          type="password"
          inputMode="text"
          value={code}
          onChange={e => setCode(e.target.value)}
          placeholder="••••"
          autoFocus
          className="w-full max-w-sm h-16 px-4 text-center text-2xl font-bold tracking-[0.5em] bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none transition-colors"
          data-testid="mobile-passcode-input"
          onKeyDown={e => e.key === 'Enter' && submit()}
        />

        <button
          onClick={submit}
          disabled={busy}
          className="w-full max-w-sm mt-6 h-14 bg-[#E52B20] hover:bg-[#C92218] active:scale-95 transition-all text-white font-bold uppercase tracking-[0.15em] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="mobile-passcode-submit"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ShieldCheck className="h-5 w-5" /> Unlock</>}
        </button>

        <p className="text-[10px] text-[#666] mt-6 max-w-sm text-center leading-relaxed">
          Bookmark this page on your phone&apos;s home screen. After unlocking, use the buttons below or open <code className="font-mono">/command-center</code> directly.
        </p>
      </div>
    </div>
  );
}

// ── Once unlocked, show big tap targets straight into the operational areas ──
function UnlockedHub() {
  const navigate = useNavigate();
  const destinations = [
    { to: '/command-center?tab=ops',          icon: Activity,    label: 'Overview',           hint: 'Pipeline + KPIs' },
    { to: '/command-center?tab=dailyops',     icon: Activity,    label: 'Daily Ops',          hint: 'Roll-up + 7-Pillar P&L' },
    { to: '/command-center?tab=quotebookings',icon: Calendar,    label: 'Quotes & Bookings',  hint: 'Intake · bookings · rentals' },
    { to: '/command-center?tab=contracts',  icon: Calendar,    label: 'Calendar',           hint: 'Revenue calendar · contracts · invoices' },
    { to: '/command-center?tab=teampay',      icon: Users,       label: 'Team & Pay',         hint: 'Drivers · payroll · partners' },
    { to: '/command-center?tab=monthlypl',    icon: Wallet,      label: 'Monthly P&L',        hint: 'Master tracker' },
    { to: '/staff',                            icon: ShieldCheck, label: 'Staff Portal',       hint: 'Today\u2019s ops mobile' },
    { to: '/admin',                            icon: Activity,    label: 'Full Admin',         hint: 'Site editing + system' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#0A0A0A] pb-20" data-testid="mobile-admin-hub">
      <Toaster position="top-center" theme="light" />
      <div className="px-5 pt-6 pb-4 border-b border-[#E5E5E5] bg-white">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#E52B20] font-bold">ACE · Mobile</p>
        <h1 className="text-2xl font-black tracking-tight mt-1">Unlocked.</h1>
        <p className="text-xs text-[#666] mt-1">Session valid 30 days · tap to jump in</p>
      </div>

      <div className="px-5 pt-5 grid grid-cols-2 gap-3">
        {destinations.map(d => {
          const Icon = d.icon;
          return (
            <button
              key={d.to}
              onClick={() => navigate(d.to)}
              className="text-left p-4 bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] hover:shadow-[4px_4px_0px_#0A0A0A] active:scale-[0.98] transition-all"
              data-testid={`hub-${d.to.split('=').pop()}`}
            >
              <Icon className="h-5 w-5 text-[#E52B20] mb-2" strokeWidth={2} />
              <p className="text-sm font-black">{d.label}</p>
              <p className="text-[10px] text-[#666] mt-0.5 leading-tight">{d.hint}</p>
            </button>
          );
        })}
      </div>

      <div className="px-5 pt-6">
        <button
          onClick={async () => {
            try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }); } catch { /* */ }
            localStorage.removeItem('ace_admin_token');
            window.location.href = '/m';
          }}
          className="w-full h-11 border border-[#E5E5E5] hover:border-[#E52B20] hover:text-[#E52B20] bg-white text-xs font-bold uppercase tracking-[0.2em] transition-colors"
          data-testid="hub-signout"
        >
          Sign out / End mobile session
        </button>
      </div>
    </div>
  );
}
